/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  RemoteMcpServerClientScopes,
  RemoteMcpServerClientScopes$inboundSchema,
} from "./remotemcpserverclientscopes.js";

/**
 * Scope state of the protected resource a remote-backed MCP server's logins are for.
 */
export type RemoteMcpServerScopes = {
  /**
   * The resource's advertised scopes_supported from that read. Absent when unknown or empty; see advertised_scopes_known.
   */
  advertisedScopes?: Array<string> | undefined;
  /**
   * Whether a read of the resource's metadata within the last good window captured an advertised list.
   */
  advertisedScopesKnown: boolean;
  /**
   * Whether the caller may change the pin: write access to this server and every server in the project with the same upstream URL.
   */
  canPin: boolean;
  /**
   * The scope parameter of the resource's last WWW-Authenticate challenge; empty when none was seen.
   */
  challengeScopes: Array<string>;
  /**
   * One entry per client bound to the server's user session issuer.
   */
  clients: Array<RemoteMcpServerClientScopes>;
  /**
   * Whether the organization's logins consult the protected resource (challenge scopes, pin, advertised list). Off, the pin is stored but not applied.
   */
  discoveryEnabled: boolean;
  /**
   * The operator's scope pin on the protected resource, shared by servers with the same upstream URL; empty when unset.
   */
  pinnedScopes: Array<string>;
  /**
   * The protected resource: the server's upstream URL.
   */
  resourceUrl: string;
  /**
   * How many other live MCP servers in the project share this upstream URL, and so this pin.
   */
  sharedServerCount: number;
};

/** @internal */
export const RemoteMcpServerScopes$inboundSchema: z.ZodMiniType<
  RemoteMcpServerScopes,
  unknown
> = z.pipe(
  z.object({
    advertised_scopes: z.optional(z.array(z.string())),
    advertised_scopes_known: z.boolean(),
    can_pin: z.boolean(),
    challenge_scopes: z.array(z.string()),
    clients: z.array(RemoteMcpServerClientScopes$inboundSchema),
    discovery_enabled: z.boolean(),
    pinned_scopes: z.array(z.string()),
    resource_url: z.string(),
    shared_server_count: z.int(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "advertised_scopes": "advertisedScopes",
      "advertised_scopes_known": "advertisedScopesKnown",
      "can_pin": "canPin",
      "challenge_scopes": "challengeScopes",
      "discovery_enabled": "discoveryEnabled",
      "pinned_scopes": "pinnedScopes",
      "resource_url": "resourceUrl",
      "shared_server_count": "sharedServerCount",
    });
  }),
);

export function remoteMcpServerScopesFromJSON(
  jsonString: string,
): SafeParseResult<RemoteMcpServerScopes, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RemoteMcpServerScopes$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RemoteMcpServerScopes' from JSON`,
  );
}
